import { createRoot } from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import App from './App';
import CssBaseline from '@mui/material/CssBaseline';

import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';
import { applyChartTheme } from 'shared/utils/chartTheme';

applyChartTheme();

// use-persisted-state reads these localStorage keys with JSON.parse at startup, so a
// value written as a raw (non-JSON) string — e.g. an access token injected directly
// into localStorage — throws "Unexpected token … is not valid JSON" and white-screens
// the whole app. Repair such values by wrapping them as a JSON string (preserving the
// session) instead of letting the parse crash. Runs once before React mounts.
function repairPersistedJson(storageKey) {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw === null) return;
    try {
      JSON.parse(raw);
    } catch {
      localStorage.setItem(storageKey, JSON.stringify(raw));
    }
  } catch {
    /* localStorage unavailable (e.g. private mode) — nothing to repair */
  }
}

// The app's use-persisted-state keys (see pages/Authorization): the access token and
// the user type. Both hold plain strings, so JSON-wrapping a raw value is always safe.
['sbly-tools:accessToken', 'userType'].forEach(repairPersistedJson);

const shadows = [
  'none',
  '0 1px 2px 0 rgba(0,0,0,0.05)',
  '0 1px 3px 0 rgba(0,0,0,0.1), 0 1px 2px -1px rgba(0,0,0,0.1)',
  '0 4px 6px -1px rgba(0,0,0,0.1), 0 2px 4px -2px rgba(0,0,0,0.1)',
  '0 10px 15px -3px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.1)',
  ...Array(21).fill('none'),
];

const theme = createTheme({
  palette: {
    primary: { main: '#6366F1', light: '#818CF8', dark: '#4F46E5' },
    secondary: { main: '#0F172A', light: '#64748B' },
    error: { main: '#EF4444', light: '#FEF2F2' },
    warning: { main: '#F59E0B', dark: '#D97706' },
    info: { main: '#3B82F6' },
    success: { main: '#10B981', light: '#ECFDF5' },
    background: { default: '#F8FAFC', paper: '#FFFFFF' },
    text: { primary: '#0F172A', secondary: '#64748B' },
    divider: '#E2E8F0',
    action: {
      hover: 'rgba(99, 102, 241, 0.04)',
      selected: 'rgba(99, 102, 241, 0.08)',
    },
  },
  typography: {
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    h3: { fontSize: '1.75rem', fontWeight: 700, letterSpacing: '-0.025em', lineHeight: 1.2 },
    h4: { fontSize: '1.5rem', fontWeight: 700, letterSpacing: '-0.02em', lineHeight: 1.3 },
    h5: { fontSize: '1.25rem', fontWeight: 600, letterSpacing: '-0.015em', lineHeight: 1.4 },
    h6: { fontSize: '1.1rem', fontWeight: 600, letterSpacing: '-0.01em', lineHeight: 1.4 },
    subtitle1: { fontSize: '0.9375rem', fontWeight: 600, letterSpacing: '0' },
    subtitle2: { fontSize: '0.8125rem', fontWeight: 600, letterSpacing: '0.01em', textTransform: 'uppercase', color: '#64748B' },
    body1: { fontSize: '0.875rem', lineHeight: 1.6 },
    body2: { fontSize: '0.8125rem', lineHeight: 1.5 },
    caption: { fontSize: '0.75rem', letterSpacing: '0.02em', color: '#64748B' },
    overline: { fontSize: '0.6875rem', fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase' },
    button: { fontWeight: 600, letterSpacing: '0.01em', textTransform: 'none' },
  },
  shadows,
  components: {
    MuiPaper: {
      defaultProps: { elevation: 0 },
      styleOverrides: {
        root: {
          borderRadius: 12,
          border: '1px solid #E2E8F0',
          backgroundImage: 'none',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: '6px 16px',
          fontWeight: 600,
          textTransform: 'none',
          boxShadow: 'none',
          transition: 'all 0.15s ease',
          '&:hover': { boxShadow: 'none' },
          '&:focus-visible': { outline: '2px solid #6366F1', outlineOffset: 2 },
        },
        contained: {
          '&:hover': { boxShadow: 'none' },
        },
        outlined: {
          borderColor: '#E2E8F0',
          color: '#334155',
          '&:hover': { borderColor: '#CBD5E1', backgroundColor: '#F8FAFC' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          '& .MuiOutlinedInput-notchedOutline': { borderColor: '#E2E8F0' },
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#CBD5E1' },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#6366F1', borderWidth: 1.5 },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.8125rem',
          minHeight: 40,
          '&.Mui-selected': { fontWeight: 600 },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { height: 2, borderRadius: 1 },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: { borderRadius: 6, fontWeight: 500, fontSize: '0.75rem' },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          fontSize: '0.8125rem',
          borderRadius: 8,
          borderColor: '#E2E8F0',
          '&.Mui-selected': {
            backgroundColor: '#EEF2FF',
            color: '#4F46E5',
            borderColor: '#C7D2FE',
            '&:hover': { backgroundColor: '#E0E7FF' },
          },
        },
      },
    },
    MuiToggleButtonGroup: {
      styleOverrides: {
        grouped: {
          '&:not(:first-of-type)': { borderRadius: 8, marginLeft: -1 },
          '&:first-of-type': { borderRadius: 8 },
        },
      },
    },
    MuiDataGrid: {
      styleOverrides: {
        root: {
          border: '1px solid #E2E8F0',
          borderRadius: 12,
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#F8FAFC',
            borderBottom: '1px solid #E2E8F0',
            fontSize: '0.75rem',
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.04em',
            color: '#64748B',
          },
          '& .MuiDataGrid-cell': { borderColor: '#F1F5F9', fontSize: '0.8125rem' },
          '& .MuiDataGrid-row:hover': { backgroundColor: 'rgba(99, 102, 241, 0.04)' },
        },
      },
    },
    MuiTableHead: {
      styleOverrides: { root: { backgroundColor: '#F8FAFC' } },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          color: '#64748B',
          fontSize: '0.75rem',
          fontWeight: 600,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          borderBottom: '1px solid #E2E8F0',
        },
        body: {
          borderBottom: '1px solid #F1F5F9',
          fontSize: '0.8125rem',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: { '&:hover': { backgroundColor: 'rgba(99, 102, 241, 0.04)' } },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: { borderRadius: 16, padding: 8 },
      },
    },
  },
});

const container = document.getElementById('root');
const root = createRoot(container);
root.render(
  <BrowserRouter>
    <CssBaseline />
    <ThemeProvider theme={theme}>
      <App />
    </ThemeProvider>
  </BrowserRouter>,
);
